import { useEffect, type ReactNode } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';

// Scaffolder tabs we never want surfaced. Templates + Tasks are kept.
const HIDDEN_TAB_NAMES = [
  'Actions',
  'Template Editor',
  'Templating Extensions',
];

const STYLE_ID = 'scaffolder-clean-styles';

// Two scopes so effects don't leak:
//  - `scaffolder-clean`: the whole /create area except the template wizard —
//    hides the page header ("Create"). Templates/Tasks tabs live in a separate
//    tab bar and survive.
//  - `scaffolder-templates`: the template list only — additionally hides the
//    content-header row (Register Existing / Support) and the filter/search
//    column, and stretches the template grid to full width.
// Class-name prefixes are the stable Backstage/MUI ones.
const CLEANUP_CSS = `
body.scaffolder-clean header,
body.scaffolder-clean [class*="BackstageHeader-header"] { display: none !important; }
body.scaffolder-templates [class*="BackstageContentHeader-container"] { display: none !important; }
body.scaffolder-templates .MuiGrid-grid-lg-2 { display: none !important; }
body.scaffolder-templates .MuiGrid-grid-lg-10 {
  max-width: 100% !important;
  flex-basis: 100% !important;
}
/* Template card: drop the header detail/favorite icons and the owner line,
   keeping the CHOOSE button pushed to the right. */
body.scaffolder-templates [id^="viewDetail-"],
body.scaffolder-templates [id^="favorite-"],
body.scaffolder-templates [data-testid="template-card-actions--ownedby"] {
  display: none !important;
}
body.scaffolder-templates [data-testid="template-card-actions--footer"] {
  justify-content: flex-end !important;
}
`;

// The scaffolder page lives at /create, with the template list at
// /create/templates and the wizard at /create/templates/<ns>/<name>.
function scaffolderScope(): { inCreate: boolean; isTemplatesList: boolean } {
  const segments = window.location.pathname.split('/').filter(Boolean);
  const inCreate = segments[0] === 'create';
  const isWizard =
    inCreate && segments[1] === 'templates' && segments.length >= 4;
  const isTemplatesList =
    inCreate &&
    (segments.length === 1 ||
      (segments[1] === 'templates' && segments.length === 2));
  return { inCreate: inCreate && !isWizard, isTemplatesList };
}

function ScaffolderCleanupWrapper({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CLEANUP_CSS;
      document.head.appendChild(style);
    }

    const apply = () => {
      // The wizard and (mostly) the task views keep their normal layout.
      const { inCreate, isTemplatesList } = scaffolderScope();
      document.body.classList.toggle('scaffolder-clean', inCreate);
      document.body.classList.toggle('scaffolder-templates', isTemplatesList);
      document.querySelectorAll<HTMLElement>('[role="tab"]').forEach(tab => {
        if (HIDDEN_TAB_NAMES.includes(tab.textContent?.trim() ?? '')) {
          tab.style.display = 'none';
        }
      });
    };

    apply();
    // Re-run on any DOM change (covers client-side route changes, which don't
    // fire popstate) and on browser back/forward.
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('popstate', apply);
    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', apply);
      document.body.classList.remove(
        'scaffolder-clean',
        'scaffolder-templates',
      );
    };
  }, []);

  return <>{children}</>;
}

export const hideScaffolderTabsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'hide-scaffolder-tabs',
      params: { component: ScaffolderCleanupWrapper },
    }),
  ],
});
